import {
  CalendarEvents,
  DEFAULT_TIME_ZONE,
  Shift,
  shiftDurationString,
} from '@nextforce/api'
import * as Calendar from 'expo-calendar'
import * as Linking from 'expo-linking'
import { t } from 'app/i18l'
import qs from 'qs'
import { filterShiftEvents } from 'schedule/schedules'
import { getShiftEndDateTime, getShiftStartDateTime } from 'shifts/utils'

export function convertShiftDataToEventData(
  shift: Shift,
  calendarId: string
): Partial<Calendar.Event> {
  return {
    calendarId,
    title: shift.position ?? t('anyPosition'),
    location: shiftLocation(shift),
    timeZone: shift.store.tz ?? DEFAULT_TIME_ZONE,
    allDay: false,
    startDate: getShiftStartDateTime(shift),
    endDate: getShiftEndDateTime(shift),
    alarms: [{ relativeOffset: -60 }, { relativeOffset: -120 }],
    status: Calendar.EventStatus.CONFIRMED,
    notes: eventNotes(shift),
    accessLevel: Calendar.EventAccessLevel.DEFAULT,
    availability: Calendar.Availability.FREE,
  }
}

function shiftLocation(shift: Shift) {
  return `${shift.store.name} - ${shift.store.storeNumber}, ${shift.store.address}, ${shift.store.city}, ${shift.store.zipCode}`
}

interface NotesObject {
  Id: string
  'Pay amount'?: string
  Duration: string
  Position: string
  Link?: string
}

function eventNotes(shift: Shift) {
  const notesObject: NotesObject = {
    Id: shift.id,
    ...(shift.shiftAmount && { 'Pay amount': `$${shift.shiftAmount}` }),
    Duration: shiftDurationString(shift),
    Position: `${shift.position ?? t('anyPosition')}`,
    Link: Linking.createURL(
      `schedule_change?${qs.stringify({
        uri: `/v2/helpers/me/schedule/shifts/${shift.id}`,
      })}`
    ),
  }

  return toNotesString(notesObject)
}

function convertScheduleEventsToCalendarEvents(
  scheduleEvents: CalendarEvents,
  calendarId: string
): Partial<Calendar.Event>[] {
  const shifts = Object.values(scheduleEvents).flatMap(events =>
    filterShiftEvents(events).flatMap(event => event.shifts)
  )
  return shifts.map(shift => convertShiftDataToEventData(shift, calendarId))
}

export async function addEventsToCalendar(
  scheduleEvents: CalendarEvents,
  calendarId: string,
  existingEvents: Calendar.Event[] | undefined
) {
  const newCalendarEvents = convertScheduleEventsToCalendarEvents(
    scheduleEvents,
    calendarId
  )
  const eventPromises = newCalendarEvents.flatMap(eventDetails => {
    const existingEvent = existingEvents?.find(event => {
      const notesObject = toNotesObject(event.notes)
      if (!notesObject) {
        return false
      }
      return notesObject['Id'] === eventDetails.id
    })

    if (existingEvent) {
      const needsUpdate =
        existingEvent.startDate !== eventDetails.startDate ||
        existingEvent.endDate !== eventDetails.endDate ||
        existingEvent.notes !== eventDetails.notes ||
        existingEvent.location !== eventDetails.location ||
        existingEvent.title !== eventDetails.title
      if (needsUpdate) {
        return Calendar.updateEventAsync(existingEvent.id, {
          startDate: eventDetails.startDate,
          endDate: eventDetails.endDate,
          notes: eventDetails.notes,
          title: eventDetails.title,
          location: eventDetails.location,
          alarms: eventDetails.alarms,
          allDay: eventDetails.allDay,
          availability: eventDetails.availability,
        })
      } else {
        return []
      }
    } else {
      return Calendar.createEventAsync(calendarId, eventDetails)
    }
  })

  if (existingEvents && existingEvents.length > 0) {
    const newIds = newCalendarEvents.map(e => e.id)
    const deleteEventPromises = existingEvents.flatMap(existingEvent => {
      const notesObject = toNotesObject(existingEvent.notes)
      if (!notesObject) {
        return []
      }
      const eventId = notesObject['Id']
      const shouldDelete = eventId && !newIds.includes(eventId)
      if (shouldDelete) {
        return Calendar.deleteEventAsync(existingEvent.id)
      }
      return []
    })
    try {
      await Promise.all(deleteEventPromises)
    } catch (error) {
      console.log('Error deleting events ', error)
    }
  }
  try {
    const eventIds = await Promise.all(eventPromises)
    return eventIds
  } catch (error) {
    console.log('Error creating/updating events ', error)
  }
}

const toNotesString = (obj: any) =>
  Object.entries(obj)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ')

const toNotesObject = (str: string): NotesObject | undefined => {
  try {
    const notesObject = Object.fromEntries(
      str.split(', ').map(v => v.split(': '))
    )
    if (
      notesObject &&
      notesObject.hasOwnProperty('Id') &&
      notesObject.hasOwnProperty('Duration') &&
      notesObject.hasOwnProperty('Position')
    ) {
      return notesObject
    } else {
      console.warn('[CalendarSync] Event note has wrong format.', str)
    }
  } catch (err) {
    console.warn('[CalendarSync] Error parsing event notes:', str, err)
  }
}
